import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { User, Organization } from "../types/api";
import { apiService } from "../services/api";
import { identifyUser, resetUser } from "../lib/posthog";
import {
  identifyUser as sentryIdentifyUser,
  resetUser as sentryResetUser,
} from "../lib/sentry";

interface AuthContextType {
  user: User | null;
  organization: Organization | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAdmin: boolean;
  isSystemOwner: boolean;
  login: (user: User, organization: Organization | undefined, token?: string) => void;
  logout: () => Promise<void>;
  updateUser: (updates: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    let isMounted = true;

    const checkSession = async () => {
      try {
        const response = await apiService.me();
        if (isMounted) {
          setUser(response.user);
          setOrganization(response.organization || null);
          if (response.token) {
            localStorage.setItem("token", response.token);
          }
          identifyUser(
            response.user.id,
            response.user.organizationId,
            response.user.role,
            response.organization?.analyticsEnabled !== false
          );
          sentryIdentifyUser(
            response.user.id,
            response.user.organizationId,
            response.user.role,
            response.organization?.analyticsEnabled !== false
          );
        }
      } catch (error) {
        // No session or invalid session
        if (isMounted) {
          setUser(null);
          setOrganization(null);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    checkSession();

    return () => {
      isMounted = false;
    };
  }, []);

  const login = (
    userData: User,
    organizationData: Organization | undefined,
    token?: string
  ) => {
    setUser(userData);
    setOrganization(organizationData || null);
    // Clear stale browser tracking data from any previous session
    localStorage.removeItem("browserTracking_metrics");
    if (token) {
      localStorage.setItem("token", token);
    }
    identifyUser(
      userData.id,
      userData.organizationId,
      userData.role,
      organizationData?.analyticsEnabled !== false
    );
    sentryIdentifyUser(
      userData.id,
      userData.organizationId,
      userData.role,
      organizationData?.analyticsEnabled !== false
    );
  };

  const logout = async () => {
    try {
      await apiService.logout();
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      // Clear state
      setUser(null);
      setOrganization(null);
      resetUser();
      sentryResetUser();

      // Clear auth and activity tracking data
      localStorage.removeItem("token");
      localStorage.removeItem("isClockedIn");
      localStorage.removeItem("lastClockInEntry");
      localStorage.removeItem("currentSessionMetrics");
      localStorage.removeItem("browserTracking_metrics");
    }
  };

  const updateUser = (updates: Partial<User>) => {
    setUser((prev) => (prev ? { ...prev, ...updates } : prev));
  };

  const value: AuthContextType = {
    user,
    organization,
    isAuthenticated: !!user,
    isLoading,
    isAdmin: user?.role === "org_admin",
    isSystemOwner: user?.role === "system_owner",
    login,
    logout,
    updateUser,
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
